import React, { useCallback, useEffect, useState } from "react";
import { Search } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import api from "../lib/api";

export default function LabTestSearch() {
  const navigate = useNavigate();
  const { search } = useLocation();
  const [tests, setTests] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const query = new URLSearchParams(search).get("q") || "";
  const [searchQuery, setSearchQuery] = useState(query);

  useEffect(() => {
    setSearchQuery(query);
  }, [query]);

  const handleMouseEnter = useCallback((e) => {
    e.currentTarget.style.background = "rgba(255,255,255,0.35)";
  }, []);

  const handleMouseLeave = useCallback((e) => {
    e.currentTarget.style.background = "rgba(255,255,255,0.2)";
  }, []);

  // Load all lab tests
  useEffect(() => {
    const loadTests = async () => {
      try {
        const res = await api.get("/api/labtests");
        setTests(res.data.tests || []);
        setFiltered(res.data.tests || []);
      } catch (err) {
        console.error("Error fetching tests:", err);
      } finally {
        setLoading(false);
      }
    };
    loadTests();
  }, []);

  // Filter when query changes
  useEffect(() => {
    const q = searchQuery.toLowerCase();
    const filteredData = tests.filter(
      (t) =>
        t.name?.toLowerCase().includes(q) ||
        t.category?.toLowerCase().includes(q) ||
        t.description?.toLowerCase().includes(q)
    );
    setFiltered(filteredData);
  }, [searchQuery, tests]);

  return (
    <div
      className="min-vh-100"
      style={{
        backgroundColor: "#f9fafb",
        color: "#1f1f1f",
        fontFamily: "'Inter', sans-serif",
        paddingBottom: 80,
      }}
    >
      {/* Header */}
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          background: "#fff",
          padding: "12px 16px",
          boxShadow: "0 1px 6px rgba(0,0,0,0.08)",
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}
      >
        <i
          className="fi fi-br-angle-left"
          onClick={() => navigate(-1)}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          style={{ cursor: "pointer", fontSize: "20px", background: "rgba(255,255,255,0.2)", borderRadius: "50%", padding: "6px 10px" }}
        ></i>
        <div
          style={{
            flex: 1,
            background: "#f1f1f1",
            borderRadius: 8,
            display: "flex",
            alignItems: "center",
            padding: "6px 10px",
          }}
        >
          <Search size={18} color="#777" />
          <input
            type="text"
            placeholder="Search lab tests..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              border: "none",
              outline: "none",
              background: "transparent",
              width: "100%",
              marginLeft: 8,
              fontSize: 15,
            }}
          />
        </div>
      </div>

      {/* Body */}
      <div className="container py-3" style={{ maxWidth: 480 }}>
        {loading ? (
          <div className="text-center mt-5 fw-semibold text-muted">
            Loading tests...
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center mt-5 text-muted">
            No results found for “{query}”
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
              gap: 14,
              paddingTop: 8,
            }}
          >
            {filtered.map((t) => (
              <motion.div
                key={t._id}
                whileTap={{ scale: 0.97 }}
                whileHover={{ scale: 1.03 }}
                onClick={() => navigate(`/lab-tests/${t._id}`)}
                style={{
                  background: "#fff",
                  borderRadius: 16,
                  boxShadow: "0 3px 6px rgba(0,0,0,0.08)",
                  cursor: "pointer",
                  overflow: "hidden",
                  transition: "all 0.2s ease",
                }}
              >
                <img
                  src={t.image}
                  alt={t.name}
                  style={{
                    width: "100%",
                    height: 120,
                    objectFit: "cover",
                  }}
                />
                <div style={{ padding: "10px 12px" }}>
                  <h6
                    style={{
                      fontSize: 14,
                      fontWeight: 700,
                      marginBottom: 2,
                      color: "#222",
                    }}
                  >
                    {t.name}
                  </h6>
                  <small style={{ color: "#777", fontSize: 12 }}>
                    {t.category}
                  </small>
                  <div
                    style={{
                      fontWeight: 700,
                      color: "#704af6",
                      fontSize: 13,
                      marginTop: 6,
                    }}
                  >
                    ${t.price.toFixed(2)}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Floating CTA */}
      {!loading && filtered.length > 0 && (
        <motion.div
          initial={{ y: 100 }}
          animate={{ y: 0 }}
          transition={{ type: "spring", stiffness: 100 }}
          style={{
            position: "fixed",
            bottom: 20,
            left: "50%",
            transform: "translateX(-50%)",
            background: "#704af6",
            color: "white",
            borderRadius: 40,
            padding: "12px 32px",
            fontWeight: 600,
            fontSize: 15,
            boxShadow: "0 6px 12px rgba(112, 74, 246, 0.3)",
            cursor: "pointer",
          }}
          onClick={() => navigate("/lab-tests")}
        >
          Explore More Tests
        </motion.div>
      )}
    </div>
  );
}
